/**
 * DepositModal — mobile-first cyberpunk modal that lets the user pick
 * how they want to push the starting USDT into Wallet A.
 *
 * Three-plus sub-views (see the `view` state machine below):
 *
 *   1. "menu"    — the option picker (Open in wallet / WalletConnect /
 *                  QR + copy). This is the default sub-view shown on
 *                  open.
 *   2. "deeplink"— after the user picks "Open in wallet app": fires an
 *                  `<a href="tron:…">` deeplink + renders the same
 *                  address/amount/copy block as fallback when the OS
 *                  silently refuses the scheme (common for wallets that
 *                  only handle `tron:<address>` without params).
 *   3. "qr"      — large QR + copy-address + copy-amount buttons for
 *                  the second-device / manual-paste case.
 *   4. "wc"      — WalletConnect sub-view. Current implementation is a
 *                  honest *half-stub*: the UI is complete but the WC
 *                  session is not initiated inside the bundle (see
 *                  NEEDS_REVIEW note below and in the PR description).
 *                  To wire up for real:
 *                    - register a project at cloud.walletconnect.com
 *                    - set `VITE_WALLETCONNECT_PROJECT_ID`
 *                    - swap the stub `start()` for
 *                      `@walletconnect/sign-client` + the
 *                      `@tronweb3/walletconnect-tron` adapter
 *                  We deliberately did NOT pull the SDK into the bundle
 *                  on this pass — it requires Node-polyfill shims that
 *                  are fragile under Cloudflare Pages' build pipeline
 *                  and would block shipping the (working) QR /
 *                  deeplink paths.
 *
 * Compatibility reference (2025-2026):
 *   - WalletConnect v2 added TRON support in January 2026 (The Block,
 *     tron.network press release).
 *   - TronLink mobile, imToken, Trust Wallet, Bitget Wallet, TokenPocket
 *     advertise WalletConnect + TRON support; behaviour varies per app.
 *   - `tron:` URI support is inconsistent — some wallets honour only
 *     `tron:<address>` and ignore `amount` / `tokenAddress` params. We
 *     always show the raw address + amount + copy buttons as a safety
 *     net beside every active path.
 *
 * Styling: reuses `.cp-modal` / `.cp-modal-backdrop` from cyberpunk.css
 * for consistency with the rest of the cyberpunk modals. Deposit-specific classes
 * (`.cp-deposit-*`) live alongside in cyberpunk.css.
 */

import { useMemo, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import {
  ArrowLeft,
  Check,
  ChevronRight,
  Copy,
  ExternalLink,
  Link as LinkIcon,
  QrCode,
  Rocket,
  Smartphone,
  X,
} from "lucide-react";

// TRC20 USDT contract on TRON mainnet. Hard-coded on purpose — this is
// the canonical address every TRC20-aware wallet recognises, and
// swapping it would change what the user actually sends.
export const USDT_TRC20_CONTRACT = "TR7NHqjeKQxGTCi8q8ZY4pL8otSzgjLj6t";

export type DepositModalView = "menu" | "deeplink" | "qr" | "wc";

interface Props {
  /** TRON base58 address — the job's `source_tron_address`. */
  walletAddress: string;
  /** Amount in USDT (decimal, e.g. 5000 means 5,000.00 USDT). */
  amountUsdt: number;
  /** Optional starting sub-view. Defaults to "menu". */
  initialView?: DepositModalView;
  /** Called when the user explicitly marks the deposit done. */
  onDeposited?: () => void;
  /** Called when the user dismisses / skips. Routes should navigate. */
  onDismiss: () => void;
}

export function DepositModal({
  walletAddress,
  amountUsdt,
  initialView = "menu",
  onDeposited,
  onDismiss,
}: Props) {
  const [view, setView] = useState<DepositModalView>(initialView);

  const amountStr = useMemo(
    () =>
      amountUsdt.toLocaleString(undefined, {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    [amountUsdt],
  );

  // tron: URI — long-form with amount + TRC20 token address. Wallets
  // that honour the spec (TronLink mobile deeplinks, etc.) will prefill
  // everything. Wallets that don't will at least get the address.
  const tronUri = useMemo(
    () =>
      `tron:${walletAddress}?amount=${amountUsdt}&token=USDT&tokenAddress=${USDT_TRC20_CONTRACT}`,
    [walletAddress, amountUsdt],
  );

  return (
    <div
      className="cp-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="deposit-modal-title"
    >
      <div className="cp-modal cp-deposit-modal">
        <button
          type="button"
          className="cp-deposit-close"
          aria-label="Close"
          onClick={onDismiss}
        >
          <X size={14} />
        </button>

        {view === "menu" && (
          <MenuView
            amountStr={amountStr}
            onPickDeeplink={() => setView("deeplink")}
            onPickWalletConnect={() => setView("wc")}
            onPickQr={() => setView("qr")}
            onSkip={onDismiss}
          />
        )}

        {view === "deeplink" && (
          <DeeplinkView
            walletAddress={walletAddress}
            amountStr={amountStr}
            tronUri={tronUri}
            onBack={() => setView("menu")}
            onDeposited={onDeposited}
          />
        )}

        {view === "qr" && (
          <QrView
            walletAddress={walletAddress}
            amountStr={amountStr}
            tronUri={tronUri}
            onBack={() => setView("menu")}
            onDeposited={onDeposited}
          />
        )}

        {view === "wc" && (
          <WalletConnectView
            walletAddress={walletAddress}
            amountStr={amountStr}
            onBack={() => setView("menu")}
            onDeposited={onDeposited}
          />
        )}
      </div>
    </div>
  );
}

// ── Sub-view: option menu ────────────────────────────────────────────────

function MenuView({
  amountStr,
  onPickDeeplink,
  onPickWalletConnect,
  onPickQr,
  onSkip,
}: {
  amountStr: string;
  onPickDeeplink: () => void;
  onPickWalletConnect: () => void;
  onPickQr: () => void;
  onSkip: () => void;
}) {
  return (
    <>
      <h2 id="deposit-modal-title" className="cp-h1">
        Deposit {amountStr} USDT to start flow
      </h2>
      <p className="cp-hint" style={{ marginTop: 10 }}>
        Wallet A is ready. Pick how to push the starting funds in —
        everything after deposit is automated.
      </p>

      <div className="cp-deposit-options">
        <OptionButton
          icon={<Smartphone size={18} />}
          title="Open in wallet app"
          subtitle="Launches your TRON wallet on this device (TronLink / imToken / Trust / Bitget / TokenPocket)."
          recommended
          onClick={onPickDeeplink}
        />
        <OptionButton
          icon={<LinkIcon size={18} />}
          title="Connect wallet"
          subtitle="WalletConnect v2 — pops up a wallet selector, approves amount automatically. Wallet must support WC + TRON."
          onClick={onPickWalletConnect}
        />
        <OptionButton
          icon={<QrCode size={18} />}
          title="Show QR + copy"
          subtitle="Scan from another device, or copy & paste manually."
          onClick={onPickQr}
        />
      </div>

      <button
        type="button"
        className="cp-deposit-skip"
        onClick={onSkip}
      >
        Skip — I&apos;ll deposit later from the job page
      </button>
    </>
  );
}

function OptionButton({
  icon,
  title,
  subtitle,
  recommended,
  onClick,
}: {
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  recommended?: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      className={`cp-deposit-option${recommended ? " is-recommended" : ""}`}
      onClick={onClick}
    >
      <span className="cp-deposit-option-icon">{icon}</span>
      <span className="cp-deposit-option-body">
        <span className="cp-deposit-option-title">
          {title}
          {recommended && (
            <span className="cp-deposit-option-badge">recommended</span>
          )}
        </span>
        <span className="cp-deposit-option-subtitle">{subtitle}</span>
      </span>
      <ChevronRight size={16} className="cp-deposit-option-chev" />
    </button>
  );
}

// ── Sub-view: tron: deeplink ─────────────────────────────────────────────

function DeeplinkView({
  walletAddress,
  amountStr,
  tronUri,
  onBack,
  onDeposited,
}: {
  walletAddress: string;
  amountStr: string;
  tronUri: string;
  onBack: () => void;
  onDeposited?: () => void;
}) {
  return (
    <>
      <BackHeader title="Open in wallet" onBack={onBack} />

      <p className="cp-hint" style={{ marginTop: 10 }}>
        Tap below — your OS will show installed TRON wallets and hand
        off this deposit request. Some wallets only accept the address,
        so we also show the raw details for manual paste.
      </p>

      <a
        href={tronUri}
        className="cp-btn cp-btn--primary cp-btn--block cp-deposit-cta"
        style={{ marginTop: 16 }}
      >
        <ExternalLink size={14} /> Open TRON wallet
      </a>

      <p className="cp-hint" style={{ marginTop: 10, fontSize: 10 }}>
        Nothing happened? Install TronLink / imToken / Trust / Bitget /
        TokenPocket first, or fall back to the QR + copy view.
      </p>

      <AddressAmountBlock address={walletAddress} amount={amountStr} />

      <DepositFooter onBack={onBack} onDeposited={onDeposited} />
    </>
  );
}

// ── Sub-view: QR + copy ──────────────────────────────────────────────────

function QrView({
  walletAddress,
  amountStr,
  tronUri,
  onBack,
  onDeposited,
}: {
  walletAddress: string;
  amountStr: string;
  tronUri: string;
  onBack: () => void;
  onDeposited?: () => void;
}) {
  return (
    <>
      <BackHeader title="Scan to deposit" onBack={onBack} />

      <div className="cp-deposit-qr-wrap" aria-label="Deposit QR code">
        <QRCodeSVG
          value={tronUri}
          size={240}
          bgColor="transparent"
          fgColor="#00f0ff"
          level="M"
          marginSize={0}
        />
      </div>

      <p className="cp-hint" style={{ textAlign: "center" }}>
        Scan with a TRON wallet on another device, or paste the details
        below manually.
      </p>

      <AddressAmountBlock address={walletAddress} amount={amountStr} />

      <DepositFooter onBack={onBack} onDeposited={onDeposited} />
    </>
  );
}

// ── Sub-view: WalletConnect (honest half-stub) ───────────────────────────

function WalletConnectView({
  walletAddress,
  amountStr,
  onBack,
  onDeposited,
}: {
  walletAddress: string;
  amountStr: string;
  onBack: () => void;
  onDeposited?: () => void;
}) {
  const projectId = import.meta.env.VITE_WALLETCONNECT_PROJECT_ID as
    | string
    | undefined;
  const [status, setStatus] = useState<
    "idle" | "connecting" | "unconfigured" | "error"
  >(projectId ? "idle" : "unconfigured");

  function start() {
    if (!projectId) {
      setStatus("unconfigured");
      return;
    }
    setStatus("connecting");
    // Full SDK wire-up is intentionally not in this bundle — see the
    // header comment and PR description. We expose a clear "not yet
    // live" state so the button never quietly does nothing.
    window.setTimeout(() => setStatus("error"), 1400);
  }

  return (
    <>
      <BackHeader title="Connect wallet" onBack={onBack} />

      <p className="cp-hint" style={{ marginTop: 10 }}>
        WalletConnect v2 gained official TRON support in January 2026.
        Compatible wallets: TronLink mobile, imToken, Trust Wallet,
        Bitget Wallet, TokenPocket. On mobile, WalletConnect will
        deeplink straight into the wallet you pick.
      </p>

      {status === "unconfigured" && (
        <div className="cp-warn" style={{ marginTop: 16 }}>
          WalletConnect project ID is not configured on this deploy.
          Set <code>VITE_WALLETCONNECT_PROJECT_ID</code> in Cloudflare
          Pages env (grab one free at{" "}
          <a
            href="https://cloud.walletconnect.com"
            target="_blank"
            rel="noreferrer"
            className="cp-link"
          >
            cloud.walletconnect.com
          </a>
          ) and redeploy. For now, use <em>Open in wallet app</em> or{" "}
          <em>Show QR + copy</em>.
        </div>
      )}

      {status === "connecting" && (
        <div className="cp-deposit-wc-loading">
          <span className="cp-spinner" /> Opening WalletConnect…
        </div>
      )}

      {status === "error" && (
        <div className="cp-warn" style={{ marginTop: 16 }}>
          WalletConnect session could not be initiated from this build
          (NEEDS_REVIEW — full SDK wire-up pending). Please fall back
          to <em>Open in wallet app</em> or <em>Show QR + copy</em>.
        </div>
      )}

      {status === "idle" && (
        <button
          type="button"
          className="cp-btn cp-btn--primary cp-btn--block cp-deposit-cta"
          style={{ marginTop: 16 }}
          onClick={start}
        >
          <LinkIcon size={14} /> Start WalletConnect session
        </button>
      )}

      <div className="cp-deposit-wc-meta">
        <div className="cp-deposit-kv-row">
          <span className="cp-deposit-meta-k">Amount</span>
          <span className="cp-deposit-meta-v">{amountStr} USDT</span>
        </div>
        <div className="cp-deposit-kv-row">
          <span className="cp-deposit-meta-k">Recipient</span>
          <span className="cp-deposit-meta-v cp-deposit-meta-mono">
            {walletAddress}
          </span>
        </div>
      </div>

      <DepositFooter onBack={onBack} onDeposited={onDeposited} />
    </>
  );
}

// ── Shared bits ──────────────────────────────────────────────────────────

function BackHeader({ title, onBack }: { title: string; onBack: () => void }) {
  return (
    <div className="cp-deposit-back-header">
      <button
        type="button"
        className="cp-btn cp-btn--small"
        onClick={onBack}
        aria-label="Back to options"
      >
        <ArrowLeft size={12} /> Back
      </button>
      <h2 className="cp-h1" style={{ fontSize: 16 }}>
        {title}
      </h2>
      <span style={{ width: 60 }} />
    </div>
  );
}

function AddressAmountBlock({
  address,
  amount,
}: {
  address: string;
  amount: string;
}) {
  return (
    <div className="cp-deposit-kv">
      <CopyRow label="Address" value={address} copyable={address} mono />
      <CopyRow label="Amount" value={`${amount} USDT`} copyable={amount} />
      <div className="cp-deposit-kv-row">
        <span className="cp-deposit-meta-k">Token</span>
        <span className="cp-deposit-meta-v">USDT (TRC20)</span>
      </div>
      <div className="cp-deposit-kv-row">
        <span className="cp-deposit-meta-k">Network</span>
        <span className="cp-deposit-meta-v">TRON</span>
      </div>
    </div>
  );
}

function CopyRow({
  label,
  value,
  copyable,
  mono,
}: {
  label: string;
  value: string;
  copyable: string;
  mono?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  async function doCopy() {
    try {
      await navigator.clipboard.writeText(copyable);
      setCopied(true);
      setTimeout(() => setCopied(false), 1400);
    } catch {
      /* user can long-press to copy as fallback */
    }
  }
  return (
    <div className="cp-deposit-kv-row">
      <span className="cp-deposit-meta-k">{label}</span>
      <span
        className={`cp-deposit-meta-v${mono ? " cp-deposit-meta-mono" : ""}`}
      >
        {value}
      </span>
      <button
        type="button"
        className="cp-btn cp-btn--small cp-deposit-copy"
        onClick={doCopy}
        aria-label={`Copy ${label}`}
      >
        {copied ? (
          <>
            <Check size={12} /> Copied
          </>
        ) : (
          <>
            <Copy size={12} /> Copy
          </>
        )}
      </button>
    </div>
  );
}

function DepositFooter({
  onBack,
  onDeposited,
}: {
  onBack: () => void;
  onDeposited?: () => void;
}) {
  return (
    <div className="cp-deposit-footer">
      <button
        type="button"
        className="cp-btn cp-btn--small"
        onClick={onBack}
      >
        <ArrowLeft size={12} /> Back to options
      </button>
      {onDeposited && (
        <button
          type="button"
          className="cp-btn cp-btn--primary"
          onClick={onDeposited}
        >
          <Rocket size={12} /> I&apos;ve deposited
        </button>
      )}
    </div>
  );
}
